import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { BoundingBox } from './BoundingBox.js';
import { StepState } from './extraction-progress.js';
import { computeAchieved } from './utils/points-model.js';

enum SubmissionState {
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
}

export enum TaskGradingState {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
}

@Entity()
export class Submission extends MongoEntity {
  static override _class = 'Submission';
  static override _collectionName = 'submissions';

  @Field(() => String)
  examId!: string;

  @Field(() => String)
  studentId: Maybe<string>;

  @Field(() => String)
  studentName: Maybe<string>;

  @Field(() => String)
  submittedAt: Maybe<string>;

  @Field(() => String)
  state!: SubmissionState;

  /**
   * Whether this submission's page split has been confirmed and is therefore locked for editing in
   * the submission-check canvas. Set to true for the whole current batch when the user confirms the
   * split; submissions created later (re-uploads) start out false so only they remain editable.
   */
  @Field(() => Boolean)
  splitConfirmed: Maybe<boolean>;

  /**
   * The `submissionSplit` job row that created this submission, so that row's own retry can delete what its
   * previous attempt made instead of duplicating the whole cohort. Absent on submissions created before the
   * split became a job, and on any created by a path that is not the split.
   */
  @Field(() => String)
  splitJobId: Maybe<string>;

  @Field(() => [SubmissionTask])
  taskSubmissions: Maybe<SubmissionTask[]>;

  /**
   * DERIVED, never stored: the student's total awarded points across the whole submission — the sum of
   * every task submission's achieved subtree total. Computed on read by the points model (mirrors
   * `ExamTask.aggregateMaxPoints` on the exam side), so it can never go stale and needs no sync.
   */
  @Field(() => Float, {
    resolve(this: any) {
      const submission = this;
      return () =>
        (Array.isArray(submission.taskSubmissions) ? submission.taskSubmissions : []).reduce(
          (sum: number, task: any) => sum + computeAchieved(task),
          0,
        );
    },
  })
  achievedPoints!: Maybe<number>;

  @Field(() => [String])
  imageIds!: Maybe<string[]>;

  /**
   * This submission's answers were typed on the student exam page, not read off a scan — so
   * `taskSubmissions` is authored data, not an extraction result. Re-running answer extraction over it
   * would throw that away and replace each answer with an OCR reading of a rendering of itself, which can
   * only ever be worse than the text it started from. Guarded in `processSubmissionById`.
   *
   * Absent on every submission that came from an upload, which is the correct reading of "not online".
   */
  @Field(() => Boolean)
  answersSubmittedOnline: Maybe<boolean>;

  /** ID of the user who created/owns this submission */
  @Field(() => String)
  userID: Maybe<string>;

  /**
   * THE DOCUMENT THAT WAS HANDED IN — the exam's own source compiled with this student's answers printed
   * into it (see student-exam.ts). Only online submissions have one; a scanned submission's equivalent is
   * the upload itself. `imageIds` are its pages.
   */
  @Field(() => String)
  submissionPdfId: Maybe<string>;

  @Field(() => String)
  gradingReportPdfId: Maybe<string>;

  @Field(() => String)
  gradingReportGeneratedAt: Maybe<string>;

  @Field(() => splitEvaluationResult)
  splitEvaluationResult!: splitEvaluationResult;

  /**
   * Per-student outcome of Fehlerindex-based grammar grading: the aggregate FI over all grammar
   * tasks and the resulting grade-step deduction on the overall exam grade. Recomputed after
   * grading runs, FI-settings changes, and manual grammar-error edits. Null in language-use-rule
   * mode (grading happens inside the grading agent instead) and for exams without grammar tasks.
   */
  @Field(() => GrammarGradingSummary)
  grammarGradingSummary: Maybe<GrammarGradingSummary>;

  /**
   * Per-submission grading results for multi-task language-use rules (LanguageUseScheme entries whose
   * dependsOn spans more than one task). Stored once per group here — referenced by
   * languageUseSchemeId — rather than duplicated onto every linked task's GradingResult, since the
   * grade covers the combined answer across the whole group.
   */
  @Field(() => [LanguageUseRuleResult])
  languageUseRuleResults: Maybe<LanguageUseRuleResult[]>;
}

/** See Submission.languageUseRuleResults. One entry per graded LanguageUseScheme rule, solo or multi-task. */
@Entity()
export class LanguageUseRuleResult extends AdornisEntity {
  static override _class = 'LanguageUseRuleResult';

  @Field(() => String)
  _id!: string;

  /** References the Exam-level LanguageUseScheme._id this result was graded against. */
  @Field(() => String)
  languageUseSchemeId!: string;

  @Field(() => Float)
  score!: number;

  @Field(() => String)
  explanation!: string;

  @Field(() => Float)
  confidence!: number;

  @Field(() => String)
  reviewerFeedback: Maybe<string>;

  @Field(() => Boolean)
  isApproved: Maybe<boolean>;

  @Field(() => Float)
  actualPointsGiven: Maybe<number>;

  @Field(() => String)
  computedAt: Maybe<string>;
}

/** See Submission.grammarGradingSummary. Grades are labels of the exam's grading scale. */
@Entity()
export class GrammarGradingSummary extends AdornisEntity {
  static override _class = 'GrammarGradingSummary';

  @Field(() => Float)
  errorCount!: number;

  @Field(() => Float)
  wordCount!: number;

  /** Aggregate Fehlerindex: total errors / total words * 100 across all grammar tasks. */
  @Field(() => Float)
  fehlerindex!: number;

  /** Whether the FI deduction was enabled when this summary was computed. */
  @Field(() => Boolean)
  fiApplied!: boolean;

  @Field(() => Float)
  deductionSteps!: number;

  /** Grade from points alone, before the FI deduction. */
  @Field(() => String)
  baseGrade!: Maybe<string>;

  /** Grade after the FI deduction (equals baseGrade when fiApplied is false or 0 steps). */
  @Field(() => String)
  adjustedGrade!: Maybe<string>;

  @Field(() => String)
  computedAt!: Maybe<string>;
}

@Entity()
export class SubmissionTask extends AdornisEntity {
  static override _class = 'SubmissionTask';

  @Field(() => String)
  _id!: string;

  @Field(() => String)
  taskNumber!: string;

  @Field(() => [String])
  imageIds!: Maybe<string[]>;

  @Field(() => String)
  studentAnswer!: Maybe<string>;

  @Field(() => GradingResult)
  gradingResult!: Maybe<GradingResult>;

  @Field(() => [BoundingBox])
  boundingBoxes!: Maybe<BoundingBox[]>;

  @Field(() => String)
  gradingState!: TaskGradingState;

  @Field(() => [SubmissionTask])
  subTasksSubmission!: Maybe<SubmissionTask[]>;

  @Field(() => GradingResult)
  overallGradingResult: Maybe<GradingResult>;

  /**
   * Grammar/spelling evaluation for essay-type tasks. Populated by the (fire-and-forget) grammar
   * pipeline that runs after grading: LanguageTool finds the errors, an LLM agent locates them on
   * the student image. Stored here (sibling of gradingResult) — not inside GradingResult — because
   * GradingResult is rebuilt wholesale on every regrade and would clobber this.
   */
  @Field(() => GrammarEvaluation)
  grammarEvaluation!: Maybe<GrammarEvaluation>;

  /**
   * Cumulative changed-word count from answer-text edits since the last time it crossed the teacher's
   * Teacher.grammarPreferences.contentRegradeWordCutoff and auto-triggered a content regrade (which
   * resets it to 0). See update-submission-task.ts's countChangedWords.
   */
  @Field(() => Int, { default: v => v ?? 0 })
  pendingContentWordDelta!: number;

  /**
   * Same accumulator as pendingContentWordDelta, independent counter, gated against
   * Teacher.grammarPreferences.languageUseRegradeWordCutoff instead — the two cutoffs fire and reset
   * at different totals (the content cutoff defaults to 0, i.e. fires on every edit, which would reset
   * a SHARED counter before the language-use total ever had a chance to accumulate).
   */
  @Field(() => Int, { default: v => v ?? 0 })
  pendingLanguageUseWordDelta!: number;

  /**
   * DERIVED, never stored: the awarded total for this task's whole subtree — its own awarded points (from
   * `gradingResult`) plus the aggregate of every child. The submission-side mirror of
   * `ExamTask.aggregateMaxPoints`; computed on read by the points model so it can never go stale.
   */
  @Field(() => Float, {
    resolve(this: any) {
      const node = this;
      return () => computeAchieved(node);
    },
  })
  aggregateAchievedPoints!: Maybe<number>;
}

@Entity()
export class subSubmissionTask extends SubmissionTask {
  static override _class = 'subSubmissionTask';
}

@Entity()
export class splitEvaluationResult extends AdornisEntity {
  static override _class = 'splitEvaluationResult';

  @Field(() => Float)
  confidence!: number;

  @Field(() => [String])
  issues!: string[];
}
@Entity()
export class GradingResult extends AdornisEntity {
  static override _class = 'GradingResult';

  @Field(() => Float)
  score!: number;

  @Field(() => String)
  explanation!: string;

  @Field(() => String)
  confidence!: string;

  @Field(() => String)
  reviewerFeedback!: Maybe<string>;

  @Field(() => Boolean)
  isApproved!: Maybe<boolean>;

  @Field(() => Float)
  actualPointsGiven!: number;

  /** Provenance of this content score — who/what last set `score`/`explanation`. Purely informational (UI badge). */
  @Field(() => String)
  gradingSource: Maybe<'ai_first_pass' | 'ai_consistency_adjusted' | 'manual'>;
}

/**
 * A single grammar/spelling error from LanguageTool, optionally located on the student image.
 * `boundingBox` holds PERCENTAGE coordinates (0–100, topLeft/bottomRight) — not pixels — matching
 * the convention the grading/structure canvases use for overlays. It is null when the coordinate
 * agent could not locate the error (the error is still listed, just without an on-image underline).
 */
@Entity()
export class GrammarError extends AdornisEntity {
  static override _class = 'GrammarError';

  @Field(() => String)
  _id!: string;

  /** Character offset of the error in the evaluated answer text. */
  @Field(() => Float)
  offset!: number;

  /** Length (in characters) of the erroneous span. */
  @Field(() => Float)
  length!: number;

  /** Human-readable description of the error (LanguageTool `message`). */
  @Field(() => String)
  message!: string;

  /** Short label for the error (LanguageTool `shortMessage`). */
  @Field(() => String)
  shortMessage!: Maybe<string>;

  /** LanguageTool rule id, e.g. 'GERMAN_SPELLER_RULE'. */
  @Field(() => String)
  ruleId!: string;

  /** LanguageTool rule category id, e.g. 'TYPOS', 'GRAMMAR', 'PUNCTUATION'. */
  @Field(() => String)
  category!: string;

  /** Surrounding context snippet (LanguageTool `context.text`); also fed to the coordinate agent. */
  @Field(() => String)
  context!: Maybe<string>;

  /** On-image location (percentage coords) for the red underline; null if not located. */
  @Field(() => BoundingBox)
  boundingBox!: Maybe<BoundingBox>;
}

@Entity()
export class GrammarEvaluation extends AdornisEntity {
  static override _class = 'GrammarEvaluation';

  @Field(() => String)
  status!: StepState;

  @Field(() => [GrammarError])
  errors!: Maybe<GrammarError[]>;

  @Field(() => Float)
  errorCount!: number;

  @Field(() => Float)
  wordCount!: number;

  /** German "Fehlerindex": errors per 100 words. Display only — does not affect the score. */
  @Field(() => Float)
  fehlerindex!: number;

  /** LanguageTool language code used, e.g. 'de-DE'. */
  @Field(() => String)
  languageCode!: Maybe<string>;

  /** Cost of the coordinate-agent LLM call. */
  @Field(() => Float)
  cost!: Maybe<number>;

  @Field(() => String)
  evaluatedAt!: Maybe<string>;
}
